import React, { useContext, useEffect, useState, useCallback } from "react";

//MUI
import Button from "@mui/material/Button";
import HistoryIcon from "@mui/icons-material/History";
import UpdateIcon from "@mui/icons-material/Update";

//Custom
import MapContext from "@/components/Map/MapContext";

import ButtonControl from "@/components/Map/Controls/Custom/Button/ButtonControl";
import ButtonGroupControl from "@/components/Map/Controls/Custom/ButtonGroup/ButtonGroupControl";

//Types
import { MapContextType } from "@/@types/context/MapContext";
import { IMapView } from "@/@types/common";
import MapEvent from "ol/MapEvent";
import { EventsKey } from "ol/events";
import { useTranslation } from "react-i18next";
import { ViewHistoryState } from "@/@types/components/Map/Controls/Custom";
import UserContext from "@/components/UserContext/UserContext";

function ViewHistoryControl() {
  const userContext = useContext(UserContext);

  const [ready, setReady] = useState(false);

  const [eventKey, setEventKey] = useState<EventsKey | null>(null);

  const [viewsState, setViewsState] = useState<ViewHistoryState>({
    views: [],
    currentViewIndex: 0,
    viewSwitchClicked: false,
    navigateTo: undefined
  });

  const mapContext = useContext<MapContextType | null>(MapContext);

  const { t } = useTranslation();

  const handlePrevious = useCallback(() => {
    setViewsState((prevState) => {
      if (prevState.currentViewIndex === 0) {
        return prevState;
      } else {
        return {
          views: prevState.views,
          currentViewIndex: prevState.currentViewIndex - 1,
          viewSwitchClicked: true,
          navigateTo: prevState.views[prevState.currentViewIndex - 1 - 1]
        };
      }
    });
  }, []);

  const handleNext = useCallback(() => {
    setViewsState((prevState) => {
      if (prevState.currentViewIndex === prevState.views.length) {
        return prevState;
      } else {
        return {
          views: prevState.views,
          currentViewIndex: prevState.currentViewIndex + 1,
          viewSwitchClicked: true,
          navigateTo: prevState.views[prevState.currentViewIndex - 1 + 1]
        };
      }
    });
  }, []);

  const navigateToView = (view: IMapView) => {
    if (mapContext?.map) {
      const v = mapContext.map.getView();
      v.animate({
        center: view.center,
        zoom: view.zoom,
        duration: 500
      });
    }
  };

  const handleMoveEnd = useCallback(
    (evt: MapEvent) => {
      if (evt?.map) {
        let mapState = {
          center: evt.map.getView().getCenter(),
          zoom: evt.map.getView().getZoom()
        }
        // save map state to localStorage
        localStorage.setItem('map_state_' + userContext?.firstName.toLowerCase(), JSON.stringify(mapState));

        const view = evt.map.getView();
        const center = view.getCenter();
        const zoom = view.getZoom();
        if (center && zoom) {
          const newView: IMapView = {
            center: center as [number, number],
            zoom: zoom
          };
          setViewsState((prevState) => {
            if (prevState.viewSwitchClicked) {
              return {
                ...prevState,
                viewSwitchClicked: false
              };
            } else {
              return {
                views: prevState.views.slice(0, prevState.currentViewIndex).concat([newView]),
                currentViewIndex: prevState.currentViewIndex + 1,
                viewSwitchClicked: false,
                navigateTo: undefined
              };
            }
          });
        }
      }
    },
    [viewsState.viewSwitchClicked]
  );

  useEffect(() => {
    if (!ready) {
      if (mapContext?.map !== null) {
        setReady(true);
      }
    }
  }, [mapContext?.map]);

  useEffect(() => {
    if (mapContext?.map) {
      const evKey = mapContext.map.on("moveend", handleMoveEnd);
      setEventKey(evKey);
    }

    return () => {
      if (mapContext?.map && eventKey) {
        mapContext.map.un("moveend", eventKey.listener);
      }
    };
  }, [mapContext?.map]);

  useEffect(() => {
    if (viewsState.navigateTo) {
      navigateToView(viewsState.navigateTo);
    }
  }, [viewsState.navigateTo]);

  const isFirstView = viewsState.currentViewIndex === 1 ? true : false;
  const isLastView = viewsState.currentViewIndex === viewsState.views.length ? true : false;

  return ready ? (
    <ButtonGroupControl id="bar-history">
      <ButtonControl id="history-prev" disabled={isFirstView} onClick={handlePrevious}>
        <HistoryIcon fontSize="small" />
      </ButtonControl>
      <ButtonControl id="history-next" disabled={isLastView} onClick={handleNext}>
        <UpdateIcon fontSize="small" />
      </ButtonControl>
    </ButtonGroupControl>
  ) : null;
}

export default ViewHistoryControl;
